import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, Lock, FileSignature, CheckCircle2, AlertCircle, ArrowLeftRight
} from 'lucide-react';
import { ndaPageContent } from '../data/mock';

const NDAPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-20 w-64 h-64 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mx-auto mb-8">
            <FileSignature className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f1f2e] mb-6">
            {ndaPageContent.headline}
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {ndaPageContent.subheadline}
          </p>
        </div>
      </section>

      {/* Why NDA Matters */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0f1f2e] mb-4">
              Why Confidentiality is Non-Negotiable
            </h2>
            <p className="text-lg text-slate-600">
              For IP risk-shifting to work, both parties need legal protection
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Why Required */}
            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mb-6">
                  <Lock className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0f1f2e] mb-4">
                  {ndaPageContent.whyRequired.title}
                </h3>
                <ul className="space-y-3">
                  {ndaPageContent.whyRequired.points.map((point, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-sm leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Approach */}
            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-emerald-500 rounded-2xl flex items-center justify-center mb-6">
                  <ArrowLeftRight className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0f1f2e] mb-4">
                  {ndaPageContent.principles.title}
                </h3>
                <ul className="space-y-3">
                  {ndaPageContent.principles.points.map((point, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-sm leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Next Steps */}
            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mb-6">
                  <FileSignature className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0f1f2e] mb-4">
                  {ndaPageContent.nextSteps.title}
                </h3>
                <ul className="space-y-3">
                  {ndaPageContent.nextSteps.points.map((point, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-[#1e3a5f] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs font-medium">{idx + 1}</span>
                      </div>
                      <span className="text-slate-600 text-sm leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Card className="bg-white border-slate-200 shadow-lg">
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-[#0f1f2e] mb-2">
                  The Process
                </h3>
                <p className="text-slate-600">
                  NDA is step 2 in our evaluation process
                </p>
              </div>

              <div className="space-y-6">
                {[
                  { step: 1, title: 'Evaluation Request', description: 'Submit non-confidential overview', active: false },
                  { step: 2, title: 'NDA Execution', description: 'Establish mutual confidentiality', active: true },
                  { step: 3, title: 'Deep Evaluation', description: 'Detailed technical discussion', active: false },
                  { step: 4, title: 'Funding Decision', description: 'Determine if concept merits investment', active: false }
                ].map((item) => (
                  <div key={item.step} className={`flex items-start space-x-4 p-4 rounded-xl ${
                    item.active ? 'bg-[#1e3a5f] text-white' : 'bg-slate-50'
                  }`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      item.active ? 'bg-white text-[#1e3a5f]' : 'bg-slate-200 text-slate-600'
                    }`}>
                      <span className="font-bold">{item.step}</span>
                    </div>
                    <div>
                      <h4 className={`font-semibold ${item.active ? 'text-white' : 'text-[#0f1f2e]'}`}>
                        {item.title}
                      </h4>
                      <p className={`text-sm ${item.active ? 'text-slate-200' : 'text-slate-500'}`}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-8 border-t border-slate-100">
                <Link to="/request-access">
                  <Button className="w-full h-14 bg-[#1e3a5f] hover:bg-[#2a4a73] text-white text-base font-semibold shadow-lg shadow-[#1e3a5f]/20 transition-all duration-300">
                    Start with Evaluation Request
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <p className="text-center text-sm text-slate-500 mt-4">
                  NDA comes after initial review confirms potential fit
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Note */}
          <Card className="bg-amber-50 border-amber-200 mt-8">
            <CardContent className="p-5">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium mb-1">Note</p>
                  <p className="text-sm text-amber-700">
                    Our NDA is a standard mutual confidentiality agreement. If your organization requires use of your own template, please mention this during the evaluation process.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default NDAPage;
